import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getGiftCode,
  createGiftCode,
  updateGiftCode,
} from "@/services/firebase/giftCode";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/hooks/useAuth";
import {
  AdminPage,
  AdminPageHeader,
  AdminLoadingSkeleton,
} from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, Loader2 } from "lucide-react";

const giftCodeSchema = z
  .object({
    code: z
      .string()
      .min(6, "代碼至少需要6個字元")
      .max(12, "代碼最多12個字元")
      .regex(/^[A-Z0-9]+$/, "代碼只能包含大寫英文字母和數字"),
    coins: z.number().min(1, "金幣數量至少為1").max(10000, "金幣數量最多為10000"),
    description: z.string().optional(),
    total_uses: z.number().min(1, "總使用次數至少為1").max(999999, "總使用次數最多為999999"),
    usage_limit_per_user: z.number().min(1, "每人限用次數至少為1").max(10, "每人限用次數最多為10"),
    valid_from: z.date({ required_error: "生效時間為必填" }),
    valid_until: z.date({ required_error: "失效時間為必填" }),
  })
  .refine(
    (data) => {
      return new Date(data.valid_until) > new Date(data.valid_from);
    },
    {
      message: "失效時間必須晚於生效時間",
      path: ["valid_until"],
    }
  );

type GiftCodeFormData = z.infer<typeof giftCodeSchema>;

export default function GiftCodeEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isNew = id === "new" || id === undefined;
  const actualId = isNew ? undefined : id?.replace("/edit", "").replace("edit", "");

  // 載入禮物代碼數據
  const { data: giftCode, isLoading } = useQuery({
    queryKey: ["gift-code", actualId],
    queryFn: () => getGiftCode(actualId!),
    enabled: !isNew && !!actualId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<GiftCodeFormData>({
    resolver: zodResolver(giftCodeSchema),
    defaultValues: {
      code: "",
      coins: 100,
      description: "",
      total_uses: 1,
      usage_limit_per_user: 1,
      valid_from: new Date(),
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    },
  });

  // 初始化表單數據
  useEffect(() => {
    if (giftCode && !isNew) {
      setValue("code", giftCode.code || "");
      setValue("coins", giftCode.coins || 100);
      setValue("description", giftCode.description || "");
      setValue("total_uses", giftCode.total_uses || 1);
      setValue("usage_limit_per_user", giftCode.usage_limit_per_user || 1);
      setValue("valid_from", giftCode.valid_from ? new Date(giftCode.valid_from) : new Date());
      setValue("valid_until", giftCode.valid_until ? new Date(giftCode.valid_until) : new Date());
    }
  }, [giftCode, setValue, isNew]);

  const mutation = useMutation({
    mutationFn: async (data: GiftCodeFormData) => {
      if (!user) {
        throw new Error("用戶未登入");
      }

      if (isNew) {
        return await createGiftCode({ ...data, used_count: 0 }, user.uid);
      } else {
        if (!actualId) {
          throw new Error("缺少代碼 ID");
        }
        await updateGiftCode(actualId, data);
        return actualId;
      }
    },
    onSuccess: (savedId) => {
      queryClient.invalidateQueries({ queryKey: ["gift-codes"] });
      queryClient.invalidateQueries({ queryKey: ["gift-code", savedId] });
      navigate("/gift-codes");
    },
    onError: (error) => {
      console.error("儲存禮物代碼失敗:", error);
      alert(`儲存失敗：${error instanceof Error ? error.message : "未知錯誤"}`);
    },
  });

  const onSubmit = (data: GiftCodeFormData) => {
    const normalizedData = {
      ...data,
      code: data.code.toUpperCase(),
    };
    mutation.mutate(normalizedData);
  };

  // 格式化日期時間為 datetime-local 輸入格式
  const formatDateTimeLocal = (date: Date | null | undefined): string => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // 解析 datetime-local 輸入為 Date
  const parseDateTimeLocal = (value: string): Date | null => {
    if (!value) return null;
    return new Date(value);
  };

  if (!isNew && isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title={isNew ? "新增禮物代碼" : "編輯禮物代碼"}
        actions={
          <Button variant="outline" onClick={() => navigate("/gift-codes")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回
          </Button>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 基本資訊 */}
        <Card>
          <CardHeader>
            <CardTitle>基本資訊</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">
                代碼 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="code"
                {...register("code")}
                placeholder="例如：NEWYEAR2024"
                style={{ textTransform: "uppercase" }}
                disabled={!isNew}
                className={errors.code ? "border-destructive" : ""}
              />
              {errors.code && (
                <p className="text-sm text-destructive">{errors.code.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                6-12位大寫英數字，建立後無法修改
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="coins">
                金幣數量 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="coins"
                type="number"
                {...register("coins", { valueAsNumber: true })}
                min="1"
                max="10000"
                placeholder="100"
                className={errors.coins ? "border-destructive" : ""}
              />
              {errors.coins && (
                <p className="text-sm text-destructive">{errors.coins.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">描述</Label>
              <Textarea
                id="description"
                {...register("description")}
                rows={3}
                placeholder="代碼用途說明（內部使用）"
              />
            </div>
          </CardContent>
        </Card>

        {/* 使用限制 */}
        <Card>
          <CardHeader>
            <CardTitle>使用限制</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="total_uses">
                總使用次數 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="total_uses"
                type="number"
                {...register("total_uses", { valueAsNumber: true })}
                min="1"
                max="999999"
                placeholder="1"
                className={errors.total_uses ? "border-destructive" : ""}
              />
              {errors.total_uses && (
                <p className="text-sm text-destructive">{errors.total_uses.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                此代碼可被兌換的總次數
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="usage_limit_per_user">
                每人限用次數 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="usage_limit_per_user"
                type="number"
                {...register("usage_limit_per_user", { valueAsNumber: true })}
                min="1"
                max="10"
                placeholder="1"
                className={errors.usage_limit_per_user ? "border-destructive" : ""}
              />
              {errors.usage_limit_per_user && (
                <p className="text-sm text-destructive">{errors.usage_limit_per_user.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                每個用戶最多可使用此代碼的次數
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 時間設定 */}
        <Card>
          <CardHeader>
            <CardTitle>有效期設定</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="valid_from">
                生效時間 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="valid_from"
                type="datetime-local"
                value={formatDateTimeLocal(watch("valid_from"))}
                onChange={(e) => setValue("valid_from", parseDateTimeLocal(e.target.value)!, { shouldValidate: true })}
                className={errors.valid_from ? "border-destructive" : ""}
              />
              {errors.valid_from && (
                <p className="text-sm text-destructive">{errors.valid_from.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="valid_until">
                失效時間 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="valid_until"
                type="datetime-local"
                value={formatDateTimeLocal(watch("valid_until"))}
                onChange={(e) => setValue("valid_until", parseDateTimeLocal(e.target.value)!, { shouldValidate: true })}
                className={errors.valid_until ? "border-destructive" : ""}
              />
              {errors.valid_until && (
                <p className="text-sm text-destructive">{errors.valid_until.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                失效時間必須晚於生效時間
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 操作按鈕 */}
        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/gift-codes")}
          >
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {mutation.isPending ? "儲存中..." : "儲存"}
          </Button>
        </div>
      </form>
    </AdminPage>
  );
}
